import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import styles from "./style.module.scss";
import {
  DeleteAdvertisementService,
  GetAllAdvertisementsService,
} from "shared/services/advertisement";

interface AdsData {
  id: number;
  screen: string;
  link: string;
  order: string;
  image: Image;
}
interface Image {
  fullpath: string;
}

interface AdvertisementListingsProps {
  handleUpdate: void | any;
}

const AdvertisementListings = ({
  handleUpdate,
}: AdvertisementListingsProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedScreen, setSelectedScreen] = useState<string>("homepage");
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    ad: AdsData | null;
  }>({
    open: false,
    action: null,
    ad: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [advertisements, setAdvertisements] = useState<AdsData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const openConfirmModal = (action: string, ad: AdsData) => {
    if (action === "Update" && handleUpdate) {
      handleUpdate(ad);
    } else {
      setConfirmModal({ open: true, action, ad });
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, ad: null });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleScreenChange = (screen: string) => {
    setSelectedScreen(screen);
    setCurrentPage(1);
  };

  const adsTableHeads = [
    { title: "Advertisement", key: "ad" },
    { title: "Screen", key: "screen" },
    { title: "Link", key: "link" },
    { title: "Order", key: "order" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllAdvertisements = async (screen: string, page: number) => {
    try {
      setLoading(true);
      const response = await GetAllAdvertisementsService(screen);
      setAdvertisements(response.data.data || []);
      setTotalItems(response.data.data.totalItems || 0);
    } catch (error) {
      console.error("Error fetching advertisements:", error);
      toastMessage("error", "Failed to fetch advertisements.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllAdvertisements(selectedScreen, currentPage);
  }, [selectedScreen, currentPage]);

  const handleDeleteAdvertisement = async (adId: number) => {
    setDeleteLoading(adId);
    try {
      await DeleteAdvertisementService(adId);
      toastMessage("success", "Advertisement deleted successfully!");
      setAdvertisements((previmages) =>
        previmages.filter((ad) => ad.id !== adId)
      );
    } catch (error) {
      console.error("Error deleting Advertisement:", error);
      toastMessage(
        "error",
        "Failed to delete Advertisement. Please try again."
      );
    } finally {
      setDeleteLoading(null);
      closeConfirmModal();
    }
  };

  return (
    <div className={styles.sectionContainer}>
      <div className="mb-4">
        <label htmlFor="screenSelect" className="mr-2">
          Select Screen:
        </label>
        <select
          id="screenSelect"
          value={selectedScreen}
          onChange={(e) => handleScreenChange(e.target.value)}
          className={styles.dropdown}
        >
          <option value="homepage">Home</option>
          <option value="search">Search</option>
          <option value="productDetail">Product Detail</option>
        </select>
      </div>

      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={adsTableHeads}
          title="Advertisements List"
          isEmpty={!loading && !advertisements.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : advertisements.map((data) => (
                <tr key={data.id}>
                  <td>
                    {data.image?.fullpath ? (
                      <img
                        src={data.image.fullpath}
                        alt="ads-img"
                        width="100"
                      />
                    ) : (
                      <p>No Image Available</p>
                    )}
                  </td>
                  <td>
                    {data.screen
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^\w/, (c) => c.toUpperCase())}{" "}
                  </td>
                  <td>{data.link}</td>
                  <td>{data.order}</td>
                  <td>
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="Update">
                        <button
                          onClick={() => openConfirmModal("Update", data)}
                        >
                          <Icons.UpdateBlog />
                        </button>
                      </CustomToolTip>
                      <CustomToolTip label="Delete">
                        <button
                          onClick={() => openConfirmModal("delete", data)}
                          disabled={deleteLoading === data.id}
                        >
                          {deleteLoading === data.id ? (
                            <SkeletonLoader />
                          ) : (
                            <Icons.Cross />
                          )}
                        </button>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />
      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title="Confirm Delete"
          actionButtonText="Yes, Delete"
          subtitle="Are you sure you want to delete this ad?"
          loading={deleteLoading === confirmModal.ad?.id}
          handleSubmit={() =>
            confirmModal.ad && handleDeleteAdvertisement(confirmModal.ad.id)
          }
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 5 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default AdvertisementListings;
